import classNames from "classnames";
import css from "./style.module.scss";
import React from "react";
import { NavBar } from "../../components/NavBar/NavBar";
import { Footer } from "../../components/Footer/Footer";
import { isDevEnvironment } from "../../utils/common";
import { GetServerSideProps } from "next";
import { useIsPageAllowed } from "../../hooks/useIsPageAllowed";
import { Domain } from "../../types/common";
import { useRouter } from "next/router";

function IndexPage({ host }: { host: string }) {
  const isDev = isDevEnvironment(host);
  const router = useRouter();

  if (!useIsPageAllowed([Domain.MEDIENECKE], host)) return false;

  return (
    <div className={classNames(css["page-layout"])}>
      <NavBar isDev={isDev} domain={Domain.MEDIENECKE} />
      <div className={classNames(css["main-layout"])}>
        <div className={classNames(css["content"])}>
          <button onClick={() => router.back()}>Zurück</button>
          <h1>Redaktionsstatut</h1>
          <p>
            Die MEDIENECKE ist das OER-Portal des KITA HUB Bayern, das digitale,
            interaktive Angebot des Staatsinstituts für Frühpädagogik und
            Medienkompetenz (IFP). Der KITA HUB ist das stetig wachsende
            Dienstleistungsangebot und die zentrale Anlaufstelle für E-Learning,
            Kommunikation und Lernmaterialien rund um Medienkompetenz im
            frühpädagogischen Arbeitsfeld und der frühkindlichen Bildung in
            Bayern. Über den KITA HUB stehen den Nutzenden verschiedene Dienste
            zur Verfügung. Diese können zur Weiterbildung, zur Information und
            zur Vernetzung und Kommunikation dienen.
          </p>
          <p>
            Sinn und Zweck der MEDIENECKE als OER-Plattform ist es, nutzbare
            Bildungsinhalte zu finden und diese gebündelt in so genannten
            „Sammlungen“ für Fachkräfte und Interessierte aus dem
            frühpädagogischen Bereich frei und öffentlich zugänglich zu machen.
            Bei der Sortierung wird unterschieden zwischen redaktionell
            betreuten Sammlungen, die sich an den Bildungsbereichen des
            Bayerischen Bildungs- und Erziehungsplans (BayBEP) orientieren sowie
            öffentlichen Sammlungen zu frühpädagogischen Querschnittsthemen.
            Beide Sammlungsformate werden von der Redaktion betreut und für
            beide Sammlungsformate gelten die Richtlinien und Qualitätskriterien
            dieses Redaktionsstatuts. Weiter ist es ein Anliegen, die
            Kuratierung, Bearbeitung und Nutzung von Open Educational Resources
            (OER) zu fördern und somit der{" "}
            <a
              href="https://www.unesco.de/bildung/open-educational-resources"
              rel="noopener noreferrer"
              target="_blank"
            >
              Empfehlung der UNESCO
            </a>{" "}
            zu folgen. OER sind freie Bildungsmaterialien jeglicher Art, die
            angepasst, verändert und weitergegeben werden können. In Kombination
            mit Creative Commons Lizenzen kann bestimmt werden, in welcher Weise
            das Material veröffentlicht und vervielfältigt werden darf.
          </p>
          <p>
            Die redaktionell betreuten Sammlungen thematisieren die zwölf
            Bildungsbereiche des BayBEP, die zu sechs Themenfeldern
            zusammengestellt wurden:
          </p>
          <ul>
            <li>Soziale und emotionale Kompetenz</li>
            <li>Bewegung & Gesundheit</li>
            <li>Sprache & Literacy</li>
            <li>Digitalisierung & Medienkompetenz</li>
            <li>Mathematik & Natur</li>
            <li>Musik & Kunst</li>
          </ul>
          <p>
            Bei der Bezeichnung dieser Bereiche wurde explizit darauf geachtet,
            die bedeutendsten Kompetenzen, welche die Kinder in der Kita
            vermittelt bekommen sollten, herauszustellen. Es wurde dadurch
            bewusst auf etwaige Abkürzungen oder Akronyme verzichtet. Vielmehr
            sollen hierdurch der inhaltliche Fokus verstärkt und eine hohe
            Nutzerfreundlichkeit erreicht werden.
          </p>
          <p>
            Neben eigens erstellten Bildungsmaterialien, die von Beschäftigten
            des IFP und deren Kooperationspartner:innen verfasst und geprüft
            werden, werden zusätzlich von der Redaktion OER-Inhalte
            recherchiert, gesammelt und qualitativ genau geprüft, bevor sie in
            der MEDIENECKE veröffentlicht werden.
          </p>
          <p>
            Mit dem vorliegenden Redaktionsstatut soll die Qualität der
            Materialien in der MEDIENECKE als Angebot des KITA HUB Bayern und
            des IFP sichergestellt werden. Es gilt gleichermaßen für alle
            Redakteur:innen und Inhaltsersteller:innen.
          </p>
          <h2>I. Selbstverständnis</h2>
          <p>
            Den Beschäftigten in Kitas, die im Arbeitsfeld der Frühpädagogik
            tätig sind, sollen durch die Zusammenstellung von spezifischen,
            Inhalten, Tools und Methoden auf einer Plattform erleichtert werden.
            Die Bildungsmaterialien werden von der Redaktion des IFP auf
            Qualität und Plausibilität geprüft sowie nach den{" "}
            <a
              href="https://www.ifp.bayern.de/veroeffentlichungen/books/bildungs-erziehungsplan/48/"
              rel="noopener noreferrer"
              target="_blank"
            >
              Grundprinzipien des BayBEP
            </a>{" "}
            (siehe S.11-23) ausgewählt. Erst dann werden sie auf der MEDIENECKE
            veröffentlicht.
          </p>
          <h2>II. Redaktionelle Grundsätze</h2>
          <ul>
            <li>
              Die Redaktion wählt die vorgeschlagenen Inhalte frei und
              selbstständig unter Berücksichtigung der Qualität aus. Positionen,
              die den{" "}
              <a
                href="https://www.ifp.bayern.de/veroeffentlichungen/books/bildungs-erziehungsplan/48/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Grundprinzipien des BayBEP
              </a>{" "}
              und des Grundgesetzes nicht entsprechen, werden nicht publiziert.
            </li>
            <li>
              Die Redaktion weist Einflussnahme und Druck von Parteien,
              Verbänden, Interessengruppen, Unternehmen und sonstigen
              ideologisch, politisch oder wirtschaftlich motivierten
              Organisationen oder Einzelpersonen auf Berichterstattung,
              Themenwahl und Darstellung zurück.
            </li>
            <li>
              Die Redaktion wählt sorgfältig und nach bestem Wissen und
              Verständnis sowie auf Grundlage inhaltlicher und fachlicher
              Korrektheit Informationsmaterial redaktionell aus. Sie orientiert
              sich an der Philosophie und den{" "}
              <a
                href="https://www.ifp.bayern.de/veroeffentlichungen/books/bildungs-erziehungsplan/48/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Grundprinzipien des BayBEP
              </a>{" "}
              sowie an den Grundsätzen der Überparteilichkeit, der Transparenz
              und der Pluralität.
            </li>
            <li>
              Die Redaktionsmitglieder lassen kontroverse Darstellungen von
              Sachverhalten zu, sie respektieren Materialien, die ihren eigenen,
              redaktionsinternen oder gesellschaftlichen Mehrheitsmeinungen
              zuwiderlaufen und bieten diesen einen Platz. Ausgenommen sind
              Positionen und Aussagen, die seitens der Redaktion als politisch
              extrem, grundgesetzwidrig und/oder menschenfeindlich angesehen
              werden.
            </li>
            <li>
              Auf unserer Seite gibt es keine Anzeigen oder andere kommerziell
              werbende Inhalte. Sollte dies trotzdem der Fall sein, zum Beispiel
              in Form einer Anleitung für ein bestimmtes Tool, wird dies klar
              gekennzeichnet und erkennbar dargestellt.
            </li>
            <li>
              Artikel und Beiträge im Rahmen von Medienpartnerschaften und
              Kooperationen werden gekennzeichnet.
            </li>
            <li>
              Berichte, Beiträge und sonstige Veröffentlichungen als
              Gegenleistung für Vergünstigungen und persönliche oder
              geschäftliche Vorteile jeder Art sind nicht zulässig.
            </li>
            <li>
              Die Redaktion folgt in ihrer Arbeitsweise den Grundideen der Open
              Education. Dazu gehören unter anderem Dialog, Verweis und
              Verlinkung, Transparenz und Ergänzungen von Originalquellen und
              -dokumenten.
            </li>
            <li>
              Werden in Materialien Kooperationen mit bestimmten Institutionen,
              Einrichtungen eingegangen, wird das im Beitrag offengelegt.
            </li>
            <li>
              Es besteht kein Rechtsanspruch auf eine Veröffentlichung von
              bestimmten Bildungsmaterialien.
            </li>
            <li>
              Alle Materialien der MEDIENECKE verfügen über eine
              Creative-Commons-Lizenz. Wir legen großen Wert darauf, dass sie
              der Öffentlichkeit frei zur Verfügung stehen – mit einem starken
              Fokus auf Wiederverwendbarkeit.
            </li>
          </ul>
          <p>
            Die MEDIENECKE ist ein Angebot des KITA HUB Bayern vom
            Staatsinstitut für Frühpädagogik und Medienkompetenz.
          </p>
          <p>
            Wenn du Anmerkungen oder Fragen zu diesem Statut hast, schreibe uns
            an{" "}
            <a href="mailto:andre.golling@ifp.bayern.de">
              andre.golling@ifp.bayern.de
            </a>
          </p>
          <p>Stand: 12.01.2024</p>
          <p>
            Dieses Statut wurde entworfen auf Basis von{" "}
            <a
              href="https://irights.info/was-ist-irights-info-redaktionsstatut"
              rel="noopener noreferrer"
              target="_blank"
            >
              https://irights.info/was-ist-irights-info-redaktionsstatut
            </a>{" "}
            und{" "}
            <a
              href="https://wirlernenonline.de/redaktionsstatut/"
              rel="noopener noreferrer"
              target="_blank"
            >
              https://wirlernenonline.de/redaktionsstatut/
            </a>{" "}
            sowie{" "}
            <a
              href="https://creativecommons.org/licenses/by/4.0/deed.de"
              rel="noopener noreferrer"
              target="_blank"
            >
              https://creativecommons.org/licenses/by/4.0/deed.de
            </a>
            .
          </p>
        </div>
      </div>
      <div>
        <Footer domain={Domain.MEDIENECKE} />
      </div>
    </div>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const host = context?.req?.headers?.host || null;

  return {
    props: { host },
  };
};

export default IndexPage;
